import { useState, useEffect, useRef } from 'react';
import { DeckState, Position } from '@/types/game';
import { fetchSampleDecksMetadata, fetchSampleDeck, SampleDeckMetadata } from '@/utils/deckLoader';

const DECK_BUILDERS: Record<string, { url: string; icon: string; label: string }> = {
  'Magic: The Gathering': {
    url: 'https://magic.c0di.com',
    icon: 'https://magic.c0di.com/app-icon.png',
    label: 'Build a deck',
  },
  Pokemon: {
    url: 'https://dexsy.c0di.com',
    icon: 'https://dexsy.c0di.com/app-icon.png',
    label: 'Build a deck',
  },
};

interface QuickDeckButtonsProps {
  onImportDeck: (deck: DeckState) => void;
  initialPosition?: Position;
  isOpen: boolean;
  onClose: () => void;
  onShowDeckImporter: () => void;
}

export default function QuickDeckButtons({ 
  onImportDeck, 
  initialPosition = { x: 200, y: 200 },
  isOpen,
  onClose,
  onShowDeckImporter
}: QuickDeckButtonsProps) {
  const [sampleDecks, setSampleDecks] = useState<SampleDeckMetadata[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    loadSampleDecks();
  }, []);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (!target || !containerRef.current) {
        return;
      }
      if (target instanceof Element && target.closest('[data-quick-deck-toggle]')) {
        return;
      }
      if (containerRef.current.contains(target)) {
        return;
      }
      onClose();
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    const listenerOptions: AddEventListenerOptions = { capture: true };

    const timer = setTimeout(() => {
      document.addEventListener('pointerdown', handlePointerDown, listenerOptions);
      document.addEventListener('keydown', handleKeyDown);
    }, 0);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('pointerdown', handlePointerDown, listenerOptions);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);
  
  const loadSampleDecks = async () => {
    setIsLoading(true);
    try {
      const decks = await fetchSampleDecksMetadata();
      setSampleDecks(decks);
    } catch (err) {
      console.error("Error loading sample decks:", err);
      setError('Failed to load quick decks');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleDeckSelect = async (filename: string) => {
    setError(null);
    try {
      const deck = await fetchSampleDeck(filename);
      
      if (!deck) {
        throw new Error('Failed to load sample deck');
      }
      
      // Set position and import the deck
      deck.position = initialPosition;
      onImportDeck(deck);
      
      // Close the picker after adding a deck
      onClose();
    } catch (err) {
      console.error("Error importing deck:", err);
      setError(err instanceof Error ? err.message : 'Failed to import deck');
    }
  };

  const handleShowImporter = () => {
    onShowDeckImporter();
    onClose();
  };
  
  if (!isOpen) {
    return null;
  }
  
  if (isLoading) {
    return (
      <div className="table-panel-strong fixed left-1/2 top-4 z-50 flex -translate-x-1/2 items-center rounded-full px-4 py-2 text-slate-900 dark:text-white">
        <div className="animate-spin mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
        <span className="text-sm">Loading decks...</span>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="fixed left-1/2 top-4 z-50 -translate-x-1/2 rounded-full bg-rose-500/90 px-4 py-2 text-sm text-white shadow-lg">
        {error}
      </div>
    );
  }
  
  return (
    <div
      ref={containerRef}
      className="table-drawer fixed bottom-0 right-0 top-0 z-[55] flex w-full flex-col overflow-hidden px-5 py-5 transition-all duration-300 sm:w-[560px]"
    >
      <div className="mb-4 flex items-start justify-between gap-4 border-b border-slate-900/8 pb-4 dark:border-white/8">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-slate-500 dark:text-slate-400">
            Deck Library
          </p>
          <h3 className="text-base font-semibold text-slate-900 dark:text-white">Start a table in one click</h3>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Drop a ready-made deck or bring in one of your own.
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="table-button-secondary rounded-full px-3 py-1.5 text-xs font-semibold"
          aria-label="Close sample deck library"
        >
          Close
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto overscroll-y-contain pb-1">
        <div className="mb-4 grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={handleShowImporter}
            className="group relative flex min-h-[132px] flex-col items-start justify-between overflow-hidden rounded-2xl border border-blue-500/24 bg-gradient-to-br from-blue-500/12 via-white/40 to-white/65 p-3 text-left shadow-sm transition duration-200 hover:-translate-y-1 hover:shadow-xl dark:from-blue-500/14 dark:via-white/4 dark:to-white/8"
            aria-label="Import your own deck"
          >
            <div className="inline-flex rounded-2xl bg-white/70 p-3 text-2xl shadow-sm dark:bg-white/8">
              <span aria-hidden="true">📥</span>
            </div>
            <div>
              <span className="block text-sm font-semibold text-slate-900 dark:text-white">
                Import Deck
              </span>
              <span className="mt-1 block text-[11px] uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                JSON or sample format
              </span>
            </div>
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-tr from-blue-500/5 via-transparent to-white/12 opacity-0 transition-opacity group-hover:opacity-100" />
          </button>

          <a
            href="https://stack.c0di.com"
            target="_blank"
            rel="noopener noreferrer"
            className="group relative flex min-h-[132px] flex-col items-start justify-between overflow-hidden rounded-2xl border border-slate-900/8 bg-white/55 p-3 text-left shadow-sm transition duration-200 hover:-translate-y-1 hover:border-blue-500/25 hover:shadow-xl dark:border-white/10 dark:bg-white/6"
            aria-label="Build a deck"
          >
            <img
              src="https://stack.c0di.com/app-icon.png"
              alt=""
              className="h-12 w-12 rounded-2xl object-cover shadow-sm"
            />
            <div>
              <span className="block text-sm font-semibold text-slate-900 dark:text-white">
                Build a deck
              </span>
              <span className="mt-1 block text-[11px] uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                Open Stack builder
              </span>
            </div>
            <div className="pointer-events-none absolute inset-0 rounded-2xl bg-gradient-to-b from-white/10 via-transparent to-blue-500/5 opacity-0 transition-opacity group-hover:opacity-100" />
          </a>
        </div>

        <div className="grid grid-cols-2 gap-3">
          {sampleDecks.length > 0 ? (
            sampleDecks.map((deck) => {
              const builder = DECK_BUILDERS[deck.name];
              return (
                <div key={deck.filename} className="flex min-w-0 flex-col">
                  <button
                    type="button"
                    onClick={() => handleDeckSelect(deck.filename)}
                    className="group relative flex flex-col items-start overflow-hidden rounded-2xl border border-slate-900/8 bg-white/55 p-3 text-left shadow-sm transition duration-200 hover:-translate-y-1 hover:border-blue-500/25 hover:shadow-xl dark:border-white/10 dark:bg-white/6"
                  >
                    <div className="mb-3 flex h-40 w-full items-center justify-center overflow-hidden rounded-xl bg-slate-950/5 shadow-md ring-1 ring-black/5 dark:bg-white/5">
                      {deck.coverImageUrl ? (
                        <img
                          src={deck.coverImageUrl}
                          alt=""
                          className="h-full aspect-[63/88] rounded-md object-contain shadow-sm transition duration-300 group-hover:scale-[1.03]"
                        />
                      ) : (
                        <div className="flex h-full aspect-[63/88] items-center justify-center rounded-md bg-gradient-to-br from-slate-100 to-slate-200 shadow-sm dark:from-slate-700 dark:to-slate-800">
                          <span className="text-3xl">🃏</span>
                        </div>
                      )}
                    </div>
                    <span className="line-clamp-2 text-sm font-semibold text-slate-900 dark:text-white">
                      {deck.name}
                    </span>
                    <span className="mt-1 text-[11px] uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                      Ready to deal
                    </span>
                    <div className="pointer-events-none absolute inset-0 rounded-2xl bg-gradient-to-b from-white/10 via-transparent to-blue-500/5 opacity-0 transition-opacity group-hover:opacity-100" />
                  </button>
                  {builder ? (
                    <a
                      href={builder.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-2 inline-flex items-center gap-2 rounded-xl bg-white/65 px-2.5 py-1.5 text-xs font-semibold text-blue-700 transition hover:bg-white dark:bg-white/8 dark:text-blue-200 dark:hover:bg-white/12"
                    >
                      <img
                        src={builder.icon}
                        alt={`${deck.name} app icon`}
                        className="h-5 w-5 rounded-md object-cover"
                      />
                      {builder.label}
                    </a>
                  ) : null}
                </div>
              );
            })
          ) : (
            <div className="rounded-2xl bg-white/30 px-4 py-3 text-sm text-slate-700 dark:bg-white/6 dark:text-slate-300">
              No quick decks available
            </div>
          )}

        </div>
      </div>
    </div>
  );
} 
